import ApiService from './ApiService';

import { FileOpener } from '@capacitor-community/file-opener'

import { Filesystem, Directory } from '@capacitor/filesystem';
import { Capacitor } from '@capacitor/core';

/**
 * Service for handling files-related API requests.
 */
class FilesService extends ApiService {

    /**
     * Downloads folder contents.
     * @param {object} folder - The folder object to download.
     * @returns {Promise<Object>} A promise that resolves to the folder contents.
     * @throws {Error} Throws an error if the request fails.
     */
    async downloadFolder(folder) {

        // Validate folder object
        if (!folder) {
            console.error('Invalid folder object');
            throw new Error('Le dossier est invalide');
        }

        // Get the folder ID
        const folderId = folder.id;

        // Validate folder ID
        if (!folderId) {
            console.error('Invalid folder ID');
            throw new Error('Identifiant de dossier invalide');
        }

        if (!folder.folder_number) {
            console.error('Invalid folder number');
            throw new Error('Le numéro de dossier est invalide');
        }

        try {
            // Get the folder contents
            const pdfContent = await this.request('GET', `template-filler/read?id_folder=${folderId}`, null, true);

            // Create a blob from the PDF content
            const blob = new Blob([pdfContent], { type: 'application/pdf' });
    
            // Create a file reader
            const reader = new FileReader();
            reader.onloadend = async () => {
                const base64Data = reader.result.split(',')[1];
                const fileName = `${folder.folder_number}.pdf`;
    
                if (Capacitor.isNativePlatform()) {
                    // Download the file to the device
                    const filePath = `${folder.folder_number}.pdf`;
    
                    try {
                        const res = await Filesystem.writeFile({
                            path: filePath,
                            data: base64Data,
                            directory: Directory.Documents,
                        });
    
                        // Open the file
                        await FileOpener.open({
                            filePath: res.uri,
                            contentType: 'application/pdf',
                        });
                    } catch (error) {
                        console.error('Erreur lors du téléchargement ou de l\'ouverture du fichier', error);
                    }
                } else {
                    // Create a blob URL and download the file
                    const fileUrl = URL.createObjectURL(blob);
                    
                    const link = document.createElement('a');
                    link.href = fileUrl;
                    link.download = fileName;
                    link.click();

                    // Revokes the object URL to free up memory
                    URL.revokeObjectURL(fileUrl);
                }
            };
    
            reader.readAsDataURL(blob);

            return true;
        } catch (error) {
            throw new Error(error?.message);
        }
    }

    /**
     * Send the folder contents by email.
     * @param {object} payload - The data for sending the folder by email.
     * @returns {Promise<Object>} A promise that resolves to the email response.
     * @throws {Error} Throws an error if the request fails.
     */
    async sendFolderByEmail(payload) {
        // Validate folder ID
        if (!payload || !payload.id_folder || !payload.id_mail_address) {
            console.error('Invalid folder ID or mail address');
            throw new Error('Identifiant de dossier ou adresse mail invalide');
        }

        try {
            // Send the folder contents by email
            await this.request('GET', `template-filler/send?id_folder=${payload.id_folder}&id_mail_address=${payload.id_mail_address}`);

            // Return the email response
            return true;
        } catch (error) {
            throw new Error(error?.message);
        }
    }
}

const filesService = new FilesService();
export default filesService;
